import { useCallback, useEffect, useState } from "react";
import { GiWheat } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroPlantio from "../../components/modais/ModalCadastroPlantio";
import ModalCadastroUnidadeMedida from "../../components/modais/ModalCadastroUnidadeMedida";
import { repositorioColheita, repositorioPlantio } from "../../sync/repositories/agricultura";
import { repositorioUnidadeMedida } from "../../sync/repositories/almoxarifado";

const AJUDA = {
  titulo: "Registro de Colheitas",
  descricao:
    "Registre aqui o resultado da colheita de um Plantio já cadastrado: quanto foi colhido, quando e em qual unidade. É esse lançamento que permite acompanhar a produtividade de cada plantio (quantidade colhida em relação à área plantada).",
  secoes: [
    {
      titulo: "Dados da Colheita",
      campos: [
        {
          nome: "Plantio",
          obrigatorio: true,
          explicacao: "A qual plantio essa colheita se refere — a lista mostra cultura, safra e talhão para facilitar a identificação. Se o plantio ainda não existe, use o botão '+' para cadastrá-lo sem sair desta tela.",
        },
        {
          nome: "Data da Colheita",
          obrigatorio: true,
          explicacao: "Data em que a colheita foi realizada em campo.",
        },
        {
          nome: "Quantidade Colhida",
          obrigatorio: true,
          explicacao: "Volume total colhido nesse lançamento, na unidade escolhida ao lado. Deve ser maior que zero.",
        },
        {
          nome: "Unidade de Medida",
          obrigatorio: true,
          explicacao: "Em que unidade a quantidade colhida foi informada (ex.: sacas, toneladas, kg). Se a unidade não existir, use o botão '+' para cadastrá-la sem sair desta tela.",
        },
        {
          nome: "Observações",
          obrigatorio: false,
          explicacao: "Informações adicionais sobre a colheita, como condições climáticas no dia ou observações sobre a produtividade.",
        },
      ],
    },
  ],
  dicas: [
    "Um mesmo plantio pode ter várias colheitas lançadas (ex.: colheita parcial em datas diferentes) — não é preciso esperar terminar toda a área para registrar.",
  ],
};

export default function GestaoColheitas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [colheitas, setColheitas] = useState([]);
  const [colheitasFiltradas, setColheitasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para os selects
  const [plantios, setPlantios] = useState([]);
  const [unidadesMedida, setUnidadesMedida] = useState([]);
  const [mostrarModalPlantio, setMostrarModalPlantio] = useState(false);
  const [mostrarModalUnidade, setMostrarModalUnidade] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: "",
    data_inicio: "",
    data_fim: ""
  });
  
  const [formData, setFormData] = useState({
    plantio: "",
    data_colheita: "",
    quantidade_colhida: "",
    unidade_medida: "",
    observacoes: "",
  });

  // Carregar plantios
  const carregarPlantios = useCallback(async () => {
    try {
      const response = await repositorioPlantio.listar();
      const dados = response.data.results || response.data;
      setPlantios(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar plantios:", error);
      setPlantios([]);
    }
  }, []);

  // Carregar unidades de medida
  const carregarUnidadesMedida = useCallback(async () => {
    try {
      const response = await repositorioUnidadeMedida.listar();
      const dados = response.data.results || response.data;
      setUnidadesMedida(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar unidades de medida:", error);
      setUnidadesMedida([]);
    }
  }, []);

  // Carregar colheitas
  const carregarColheitas = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioColheita.listar();
      const dados = response.data.results || response.data;

      setColheitas(Array.isArray(dados) ? dados : []);
      setColheitasFiltradas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar colheitas:", error);
      if (error.response?.status !== 401) {
        alert("Erro ao carregar lista de colheitas");
      }
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarColheitas();
    carregarPlantios();
    carregarUnidadesMedida();
  }, [carregarColheitas, carregarPlantios, carregarUnidadesMedida]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.plantio) {
      alert("Selecione um plantio.");
      return false;
    }
    if (!formData.data_colheita) {
      alert("Informe a data da colheita.");
      return false;
    }
    if (!formData.quantidade_colhida || Number(formData.quantidade_colhida) <= 0) {
      alert("Informe uma quantidade colhida válida.");
      return false;
    }
    if (!formData.unidade_medida) {
      alert("Selecione a unidade de medida.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const dadosEnvio = {
      plantio: formData.plantio,
      data_colheita: formData.data_colheita,
      quantidade_colhida: Number(formData.quantidade_colhida),
      unidade_medida: formData.unidade_medida,
      observacoes: formData.observacoes || "",
    };

    try {
      if (editando) {
        await repositorioColheita.atualizar(editandoId, dadosEnvio);
        alert("Colheita atualizada com sucesso!");
        handleCancel();
        carregarColheitas();
      } else {
        await repositorioColheita.criar(dadosEnvio);
        alert("Colheita cadastrada com sucesso!");
        handleCancel();
        carregarColheitas();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data) && error.response?.status !== 401) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data) && error.response?.status !== 401) {
        alert(data.join("\n"));
      } else if (error.response?.status !== 401) {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (colheita) => {
    setFormData({
      plantio: colheita.plantio?.id || colheita.plantio || "",
      data_colheita: colheita.data_colheita || "",
      quantidade_colhida: colheita.quantidade_colhida || "",
      unidade_medida: colheita.unidade_medida?.id || colheita.unidade_medida || "",
      observacoes: colheita.observacoes || "",
    });
    setEditando(true);
    setEditandoId(colheita.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaColheita = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      plantio: "",
      data_colheita: "",
      quantidade_colhida: "",
      unidade_medida: "",
      observacoes: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta colheita? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioColheita.excluir(id);
      alert("Colheita excluída com sucesso!");
      carregarColheitas();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      if (error.response?.status !== 401) {
        alert("Erro ao excluir colheita.");
      }
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(colheitas) ? [...colheitas] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c => 
        (c.nome_plantio || `${c.cultura_nome} - ${c.safra_nome}`)?.toLowerCase().includes(buscaLower) ||
        c.cultura_nome?.toLowerCase().includes(buscaLower) ||
        c.safra_nome?.toLowerCase().includes(buscaLower) ||
        c.talhao_nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.data_inicio) {
      filtrados = filtrados.filter(c => c.data_colheita >= filtros.data_inicio);
    }
    
    if (filtros.data_fim) {
      filtrados = filtrados.filter(c => c.data_colheita <= filtros.data_fim);
    }
    
    setColheitasFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", data_inicio: "", data_fim: "" });
    setColheitasFiltradas(Array.isArray(colheitas) ? colheitas : []);
  };

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const formatarQuantidade = (quantidade) => {
    if (!quantidade && quantidade !== 0) return "—";
    return Number(quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getUnidadeSigla = (unidadeMedidaId, colheita) => {
    if (colheita.unidade_medida?.sigla) {
      return colheita.unidade_medida.sigla;
    }
    if (Array.isArray(unidadesMedida)) {
      const unidade = unidadesMedida.find(u => u.id === unidadeMedidaId);
      return unidade?.sigla || "—";
    }
    return "—";
  };

  // Verificar se está autenticado
  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/';
      return false;
    }
    return true;
  };

  useEffect(() => {
    isAuthenticated();
  }, []);

  if (!isAuthenticated()) {
    return null;
  }

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Colheitas <GiWheat size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaColheita}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Colheita
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Colheita" : "Cadastrar Nova Colheita"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Plantio e Data */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Plantio *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="plantio"
                        value={formData.plantio}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione um plantio...</option>
                        {Array.isArray(plantios) && plantios.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.cultura_nome} - {p.safra_nome} (Talhão: {p.talhao_nome})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalPlantio(true)}
                        title="Cadastrar novo plantio"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data da Colheita *</label>
                    <input
                      type="date"
                      name="data_colheita"
                      value={formData.data_colheita}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    />
                  </div>
                </div>

                {/* Linha 2: Quantidade e Unidade */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Quantidade Colhida *</label>
                    <input
                      type="number"
                      name="quantidade_colhida"
                      min="0.01"
                      step="0.01"
                      value={formData.quantidade_colhida}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="0,00"
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Unidade de Medida *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="unidade_medida"
                        value={formData.unidade_medida}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione uma unidade...</option>
                        {Array.isArray(unidadesMedida) && unidadesMedida.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.sigla} - {u.descricao}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalUnidade(true)}
                        title="Cadastrar nova unidade"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Linha 3: Observações */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={3}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre a colheita (condições climáticas, produtividade, etc.)..."
                  />
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Cultura, Safra ou Talhão</label>
                  <input
                    type="text"
                    placeholder="Digite o nome da cultura, safra ou talhão..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Colheitas */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {Array.isArray(colheitasFiltradas) ? colheitasFiltradas.length : 0} colheita(s) encontrada(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Data</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Cultura</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Safra</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Talhão</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Quantidade</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Unidade</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Observações</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(colheitasFiltradas) && colheitasFiltradas.map((colheita) => (
                        <tr key={colheita.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{colheita.id}</td>
                          <td className="p-3 text-sm">{formatarData(colheita.data_colheita)}</td>
                          <td className="p-3 text-sm font-medium">{colheita.cultura_nome || "-"}</td>
                          <td className="p-3 text-sm">{colheita.safra_nome || "-"}</td>
                          <td className="p-3 text-sm">{colheita.talhao_nome || "-"}</td>
                          <td className="p-3 text-sm font-medium">
                            {formatarQuantidade(colheita.quantidade_colhida)}
                          </td>
                          <td className="p-3 text-sm">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs">
                              {getUnidadeSigla(colheita.unidade_medida, colheita)}
                            </span>
                          </td>
                          <td className="p-3 text-sm max-w-xs truncate">{colheita.observacoes || "—"}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(colheita)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(colheita.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {(!Array.isArray(colheitasFiltradas) || colheitasFiltradas.length === 0) && (
                        <tr>
                          <td colSpan="9" className="p-8 text-center text-gray-500">
                            {filtros.busca || filtros.data_inicio || filtros.data_fim
                              ? "Nenhuma colheita encontrada com os filtros selecionados."
                              : "Nenhuma colheita cadastrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroPlantio
        isOpen={mostrarModalPlantio}
        onClose={() => setMostrarModalPlantio(false)}
        onSuccess={async (novo) => {
          await carregarPlantios();
          setFormData((prev) => ({ ...prev, plantio: novo.id }));
        }}
      />
      <ModalCadastroUnidadeMedida
        isOpen={mostrarModalUnidade}
        onClose={() => setMostrarModalUnidade(false)}
        onSuccess={async (nova) => {
          await carregarUnidadesMedida();
          setFormData((prev) => ({ ...prev, unidade_medida: nova.id }));
        }}
      />
    </Layout>
  );
}